import React, { useContext, useState } from 'react';
import { ScrollView, View } from 'react-native';
import {
  Button, DataTable, Dialog, IconButton, List, Portal, Text, TextInput,
} from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { useMutation } from '@apollo/client';
import { useToast } from 'react-native-toast-notifications';
import { SCREEN_NAMES } from '../../../utils/constant';
import { CREATE_COMPANION, DeleteUserMutation } from '../../../gql/user/mutation';
import { AuthContext } from '../../../context/AuthContext';
import { ModalContext } from '../../../context/ModalContext';
import ConfirmationModal from '../../../modals/Confirmation/ConfirmationModal';
import LoadingIndicator from '../../../components/commons/LoadingIndicator';
import UserAvatarBadge from '../../../components/commons/UserAvatarBadge';
import useDesktopLayout from '../../../hooks/useDesktopLayout';

function CompanionsTab({
  users, searchQuery, loading, refetch,
}) {
  const navigation = useNavigation();
  const toast = useToast();
  const { authState } = useContext(AuthContext);
  const { displayConfirmationModal, toggleConfirmationModal } = useContext(ModalContext);
  const isSuperAdmin = authState.role === 'SUPERADMIN';
  const isDesktop = useDesktopLayout();

  const [companionDialogVisible, setCompanionDialogVisible] = useState(false);
  const [companionForm, setCompanionForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
  });

  const companions = users
    .filter((u) => u.status === 'COMPANION')
    .filter((u) => u.firstName?.toLowerCase().includes(searchQuery)
      || u.lastName?.toLowerCase().includes(searchQuery));

  const [deleteOneUser] = useMutation(DeleteUserMutation, {
    onCompleted: (_data) => {
      toast.show(_data.deleteOneUser.message);
      refetch();
    },
  });

  const [createCompanion] = useMutation(CREATE_COMPANION, {
    onCompleted: (_data) => {
      toast.show(_data.createCompanion.message);
      setCompanionDialogVisible(false);
      setCompanionForm({
        firstName: '', lastName: '', email: '', phone: '',
      });
      refetch();
    },
    onError: (err) => toast.show(err.message),
  });

  return (
    <>
      <Portal>
        <Dialog visible={companionDialogVisible} onDismiss={() => setCompanionDialogVisible(false)}>
          <Dialog.Title>Créer un accompagnateur</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Prénom"
              mode="outlined"
              value={companionForm.firstName}
              onChangeText={(v) => setCompanionForm((f) => ({ ...f, firstName: v }))}
              style={{ marginBottom: 8 }}
            />
            <TextInput
              label="Nom"
              mode="outlined"
              value={companionForm.lastName}
              onChangeText={(v) => setCompanionForm((f) => ({ ...f, lastName: v }))}
              style={{ marginBottom: 8 }}
            />
            <TextInput
              label="Email"
              mode="outlined"
              value={companionForm.email}
              onChangeText={(v) => setCompanionForm((f) => ({ ...f, email: v }))}
              keyboardType="email-address"
              autoCapitalize="none"
              style={{ marginBottom: 8 }}
            />
            <TextInput
              label="Téléphone (optionnel)"
              mode="outlined"
              value={companionForm.phone}
              onChangeText={(v) => setCompanionForm((f) => ({ ...f, phone: v }))}
              keyboardType="phone-pad"
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setCompanionDialogVisible(false)}>Annuler</Button>
            <Button
              mode="contained"
              onPress={() => createCompanion({
                variables: {
                  firstName: companionForm.firstName,
                  lastName: companionForm.lastName,
                  email: companionForm.email,
                  phone: companionForm.phone || undefined,
                },
              })}
              disabled={!companionForm.firstName || !companionForm.lastName || !companionForm.email}
            >
              Créer
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
      {displayConfirmationModal.display && <ConfirmationModal />}
      <View style={{ flex: 1 }}>
        {isSuperAdmin && (
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', paddingHorizontal: 4 }}>
            <IconButton icon="account-heart" onPress={() => setCompanionDialogVisible(true)} />
          </View>
        )}
        {loading && <LoadingIndicator text="Chargement..." />}
        <ScrollView style={{ flex: 1 }}>
          {isDesktop ? (
            <DataTable>
              <DataTable.Header>
                <DataTable.Title>{' '}</DataTable.Title>
                <DataTable.Title>Nom</DataTable.Title>
                <DataTable.Title>Email</DataTable.Title>
                <DataTable.Title numeric>Actions</DataTable.Title>
              </DataTable.Header>
              {companions.map((d) => (
                <DataTable.Row key={d.id}>
                  <DataTable.Cell><UserAvatarBadge user={d} icon="account-heart" /></DataTable.Cell>
                  <DataTable.Cell>{`${d.firstName} ${d.lastName}`}</DataTable.Cell>
                  <DataTable.Cell>{d.email}</DataTable.Cell>
                  <DataTable.Cell numeric>
                    <View style={{ flexDirection: 'row' }}>
                      <IconButton
                        icon="eye"
                        onPress={() => navigation.navigate({ name: SCREEN_NAMES.PROFILE, params: { email: d.email } })}
                      />
                      {isSuperAdmin && (
                        <IconButton
                          icon="delete"
                          onPress={() => toggleConfirmationModal({
                            title: 'Confirmation',
                            message: "Voulez-vous vraiment supprimer définitivement l'utilisateur ? Cette action est irréversible.",
                            handleSubmit: async () => { await deleteOneUser({ variables: { email: d.email } }); },
                          })}
                        />
                      )}
                    </View>
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable>
          ) : companions.map((d) => (
            <List.Item
              key={d.id}
              title={`${d.firstName} ${d.lastName}`}
              description={d.email}
              left={(props) => <UserAvatarBadge user={d} icon="account-heart" style={props.style} />}
              right={(props) => (
                <>
                  <IconButton
                    {...props}
                    icon="eye"
                    onPress={() => navigation.navigate({ name: SCREEN_NAMES.PROFILE, params: { email: d.email } })}
                  />
                  {isSuperAdmin && (
                    <IconButton
                      {...props}
                      icon="delete"
                      onPress={() => toggleConfirmationModal({
                        title: 'Confirmation',
                        message: "Voulez-vous vraiment supprimer définitivement l'utilisateur ? Cette action est irréversible.",
                        handleSubmit: async () => { await deleteOneUser({ variables: { email: d.email } }); },
                      })}
                    />
                  )}
                </>
              )}
            />
          ))}
          {companions.length === 0 && !loading && (
            <Text style={{ textAlign: 'center', marginTop: 32, opacity: 0.5 }}>
              Aucun accompagnateur trouvé
            </Text>
          )}
        </ScrollView>
      </View>
    </>
  );
}

export default CompanionsTab;
